/**
 * Period backfill (work order item 6): the rules behind the shop file's "Period backfill" tab.
 *
 * JSX-free on purpose, like the rest of `src/lib`: the dashboard's Jest runs in `node` with no
 * jsdom, so the day bounds in the shop's timezone, the selection cap and — above all — the rule
 * that arms the execute button live here, where they can be tested.
 *
 * **The server is authoritative.** The API clamps the window, applies the C2 gap predicate, signs
 * the candidate set and re-checks every order against fresh data before it mints
 * (`apps/api/src/app/internal-admin/period-backfill.service.ts`). What this module mirrors is only
 * what lets the operator hear about a mistake before the round-trip: a stale preview, a changed
 * period, too many orders, a short reason. The API refuses the same things regardless.
 */

import {
  PeriodBackfillClamp,
  PeriodBackfillExclusionReason,
  PeriodBackfillOrderOutcome,
  PeriodBackfillShopIneligibility,
  type PeriodBackfillExecuteRequestDto,
  type PeriodBackfillOrderResultDto,
  type PeriodBackfillPreviewDto,
} from '../../../../libs/api-client/src/generated/models';
import { isReasonAcceptable } from './shop-file';

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Shopify's order read window for this app, in days — `SHOPIFY_ORDER_READ_WINDOW_DAYS` in the API.
 * The app holds `read_orders` without `read_all_orders`, so older orders are unreachable.
 */
export const SHOPIFY_READ_WINDOW_DAYS = 60;

// ── Catalogue keys (literal, so the parity spec's key scan sees every one) ──

/** Plain-words title and explanation per exclusion reason — the C2 gap predicate's tokens. */
export const EXCLUSION_KEYS: Record<
  PeriodBackfillExclusionReason,
  { readonly title: string; readonly explain: string }
> = {
  [PeriodBackfillExclusionReason.NotPaid]: {
    title: 'periodBackfill.exclusions.notPaid.title',
    explain: 'periodBackfill.exclusions.notPaid.explain',
  },
  [PeriodBackfillExclusionReason.BeforeAnchor]: {
    title: 'periodBackfill.exclusions.beforeAnchor.title',
    explain: 'periodBackfill.exclusions.beforeAnchor.explain',
  },
  [PeriodBackfillExclusionReason.C3Owned]: {
    title: 'periodBackfill.exclusions.c3Owned.title',
    explain: 'periodBackfill.exclusions.c3Owned.explain',
  },
  [PeriodBackfillExclusionReason.ChannelExempt]: {
    title: 'periodBackfill.exclusions.channelExempt.title',
    explain: 'periodBackfill.exclusions.channelExempt.explain',
  },
  [PeriodBackfillExclusionReason.AlreadyInvoiced]: {
    title: 'periodBackfill.exclusions.alreadyInvoiced.title',
    explain: 'periodBackfill.exclusions.alreadyInvoiced.explain',
  },
  [PeriodBackfillExclusionReason.AwaitingMerchantInput]: {
    title: 'periodBackfill.exclusions.awaitingMerchantInput.title',
    explain: 'periodBackfill.exclusions.awaitingMerchantInput.explain',
  },
  [PeriodBackfillExclusionReason.DeclinedByMerchant]: {
    title: 'periodBackfill.exclusions.declinedByMerchant.title',
    explain: 'periodBackfill.exclusions.declinedByMerchant.explain',
  },
};

/** Why the whole shop cannot be backfilled right now. */
export const INELIGIBILITY_KEYS: Record<
  PeriodBackfillShopIneligibility,
  string
> = {
  [PeriodBackfillShopIneligibility.SubscriptionNotActive]:
    'periodBackfill.ineligible.subscriptionNotActive',
  [PeriodBackfillShopIneligibility.AvvNotAccepted]:
    'periodBackfill.ineligible.avvNotAccepted',
  [PeriodBackfillShopIneligibility.PendingDeletion]:
    'periodBackfill.ineligible.pendingDeletion',
  [PeriodBackfillShopIneligibility.InvoicingNotConfigured]:
    'periodBackfill.ineligible.invoicingNotConfigured',
};

/** What moved a bound of the period. */
export const CLAMP_KEYS: Record<PeriodBackfillClamp, string> = {
  [PeriodBackfillClamp.ShopifyReadWindow]: 'periodBackfill.clamp.readWindow',
  [PeriodBackfillClamp.InvoicingAnchor]: 'periodBackfill.clamp.anchor',
  [PeriodBackfillClamp.Now]: 'periodBackfill.clamp.now',
};

/** One order's outcome as a label. */
export const OUTCOME_KEYS: Record<PeriodBackfillOrderOutcome, string> = {
  [PeriodBackfillOrderOutcome.Minted]: 'periodBackfill.results.minted',
  [PeriodBackfillOrderOutcome.Refused]: 'periodBackfill.results.refused',
  [PeriodBackfillOrderOutcome.Failed]: 'periodBackfill.results.failed',
};

/** The refusal codes the API answers with, as copy. The server's own text for anything else. */
const ERROR_KEYS: Readonly<Record<string, string>> = {
  PERIOD_BACKFILL_PREVIEW_EXPIRED: 'periodBackfill.errors.previewExpired',
  PERIOD_BACKFILL_PREVIEW_INVALID: 'periodBackfill.errors.previewInvalid',
  PERIOD_BACKFILL_ORDER_NOT_IN_PREVIEW: 'periodBackfill.errors.notInPreview',
  PERIOD_BACKFILL_SHOP_NOT_ELIGIBLE: 'periodBackfill.errors.shopNotEligible',
};

/**
 * Our copy for a refusal the API answered with, or null to show the server's message.
 *
 * @param message - The API's `message` (`PERIOD_BACKFILL_ORDER_NOT_IN_PREVIEW: 123` included).
 */
export function errorKeyForMessage(message: string | null): string | null {
  if (!message) return null;
  const code = message.split(':')[0]?.trim() ?? '';
  return ERROR_KEYS[code] ?? null;
}

// ── Days in the shop's timezone ─────────────────────────────────────────────

/** `YYYY-MM-DD` of an instant, in a timezone. Falls back to UTC for an unknown zone. */
export function dayInZone(instant: Date, timeZone: string): string {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(instant);
  } catch {
    return instant.toISOString().slice(0, 10);
  }
}

/** Milliseconds the zone is ahead of UTC at an instant. */
function zoneOffsetMs(instant: Date, timeZone: string): number {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).formatToParts(instant);
    const get = (type: string) =>
      Number(parts.find((part) => part.type === type)?.value ?? '0');
    const asUtc = Date.UTC(
      get('year'),
      get('month') - 1,
      get('day'),
      get('hour'),
      get('minute'),
      get('second'),
    );
    return asUtc - Math.floor(instant.getTime() / 1000) * 1000;
  } catch {
    return 0;
  }
}

/**
 * The first instant of a calendar day in a timezone.
 *
 * The period is the SHOP's days, not the operator's: a merchant's "September" and its DATEV
 * export are cut in the shop timezone, and an operator in another zone must not shift the edges.
 * Two passes, so a day on which the offset changes (DST) still lands on its own midnight.
 *
 * @param day - `YYYY-MM-DD`
 * @param timeZone - IANA zone, e.g. `Europe/Berlin`
 */
export function startOfDayInZone(day: string, timeZone: string): Date {
  const [y, m, d] = day.split('-').map(Number);
  const guess = Date.UTC(y, (m ?? 1) - 1, d ?? 1);
  let instant = guess - zoneOffsetMs(new Date(guess), timeZone);
  instant = guess - zoneOffsetMs(new Date(instant), timeZone);
  return new Date(instant);
}

/** Adds whole days to a `YYYY-MM-DD` (calendar arithmetic, no zone involved). */
function addDays(day: string, days: number): string {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(Date.UTC(y, (m ?? 1) - 1, (d ?? 1) + days))
    .toISOString()
    .slice(0, 10);
}

/** The picker's limits: from the read window's first full day (or the anchor) to today. */
export interface PeriodBounds {
  readonly min: string;
  readonly max: string;
}

/**
 * The days the picker offers. The API clamps regardless; offering only reachable days is what
 * keeps the operator from asking for a month Shopify will not serve.
 *
 * @param now - The clock
 * @param timeZone - The shop's timezone
 * @param anchor - The invoicing anchor from an earlier preview, when known
 */
export function periodBounds(
  now: Date,
  timeZone: string,
  anchor: string | null,
): PeriodBounds {
  const readStart = dayInZone(
    new Date(now.getTime() - SHOPIFY_READ_WINDOW_DAYS * DAY_MS),
    timeZone,
  );
  // The read window starts mid-day; its first FULL day is the next one.
  let min = addDays(readStart, 1);
  if (anchor) {
    const anchorDay = dayInZone(new Date(anchor), timeZone);
    if (anchorDay > min) min = anchorDay;
  }
  return { min, max: dayInZone(now, timeZone) };
}

/** The default period: the last 30 days that the picker allows. */
export function defaultPeriod(bounds: PeriodBounds): {
  readonly from: string;
  readonly to: string;
} {
  const from = addDays(bounds.max, -29);
  return { from: from < bounds.min ? bounds.min : from, to: bounds.max };
}

/** The preview request for a period of whole days, or the catalogue key of what is wrong. */
export function periodRequest(
  fromDay: string,
  toDay: string,
  timeZone: string,
):
  | { readonly ok: true; readonly from: string; readonly to: string }
  | { readonly ok: false; readonly problemKey: string } {
  if (
    !/^\d{4}-\d{2}-\d{2}$/.test(fromDay) ||
    !/^\d{4}-\d{2}-\d{2}$/.test(toDay)
  ) {
    return { ok: false, problemKey: 'periodBackfill.period.incomplete' };
  }
  if (fromDay > toDay) {
    return { ok: false, problemKey: 'periodBackfill.period.reversed' };
  }
  return {
    ok: true,
    from: startOfDayInZone(fromDay, timeZone).toISOString(),
    // Inclusive end: the last millisecond of the chosen day.
    to: new Date(
      startOfDayInZone(addDays(toDay, 1), timeZone).getTime() - 1,
    ).toISOString(),
  };
}

// ── Selection ───────────────────────────────────────────────────────────────

/**
 * Toggles one order, never past the per-run cap.
 *
 * @returns The new selection (a new Set, so React sees the change).
 */
export function toggleSelection(
  selected: ReadonlySet<string>,
  orderId: string,
  max: number,
): Set<string> {
  const next = new Set(selected);
  if (next.has(orderId)) next.delete(orderId);
  else if (next.size < max) next.add(orderId);
  return next;
}

/** The first `max` candidates — the "select as many as one run takes" button. */
export function selectFirst(
  preview: Pick<PeriodBackfillPreviewDto, 'candidates' | 'maxOrdersPerRun'>,
): Set<string> {
  return new Set(
    preview.candidates
      .slice(0, preview.maxOrdersPerRun)
      .map((candidate) => candidate.orderId),
  );
}

// ── The armed execute button (AC-6.4) ───────────────────────────────────────

/** What the execute step needs to know about the page. */
export interface ExecuteReadiness {
  readonly preview: Pick<
    PeriodBackfillPreviewDto,
    'candidates' | 'maxOrdersPerRun' | 'previewToken' | 'previewExpiresAt'
  > | null;
  /** The period the preview was taken for. */
  readonly previewedPeriod: {
    readonly from: string;
    readonly to: string;
  } | null;
  /** The period the picker shows now. */
  readonly currentPeriod: { readonly from: string; readonly to: string };
  readonly selected: ReadonlySet<string>;
  readonly reason: string;
  readonly now: Date;
}

/**
 * Why execute is not armed, as a catalogue key — or null when it is.
 *
 * Armed only by a fresh preview OF THE SAME PERIOD, the way `BroadcastAlertPage` arms its send
 * button: a period changed after the preview, or a preview older than its token, must be
 * previewed again before anything is minted. The API enforces the token; this keeps the button
 * honest about it.
 */
export function executeProblem(state: ExecuteReadiness): string | null {
  const { preview } = state;
  if (!preview || !state.previewedPeriod) {
    return 'periodBackfill.execute.needsPreview';
  }
  if (
    state.previewedPeriod.from !== state.currentPeriod.from ||
    state.previewedPeriod.to !== state.currentPeriod.to
  ) {
    return 'periodBackfill.execute.periodChanged';
  }
  if (new Date(preview.previewExpiresAt).getTime() <= state.now.getTime()) {
    return 'periodBackfill.execute.previewExpired';
  }
  if (state.selected.size === 0) return 'periodBackfill.execute.noneSelected';
  if (state.selected.size > preview.maxOrdersPerRun) {
    return 'periodBackfill.execute.tooMany';
  }
  const candidates = new Set(preview.candidates.map((c) => c.orderId));
  for (const id of state.selected) {
    if (!candidates.has(id)) return 'periodBackfill.execute.notInPreview';
  }
  if (!isReasonAcceptable(state.reason)) {
    return 'periodBackfill.execute.reasonTooShort';
  }
  return null;
}

/** The execute body. Customer email is OFF unless the operator ticked it (work order A15). */
export function executeBody(input: {
  readonly previewToken: string;
  readonly selected: ReadonlySet<string>;
  readonly reason: string;
  readonly sendCustomerEmail: boolean;
}): PeriodBackfillExecuteRequestDto {
  return {
    reason: input.reason.trim(),
    previewToken: input.previewToken,
    orderIds: [...input.selected].sort(),
    sendCustomerEmail: input.sendCustomerEmail,
  };
}

/** The selected candidates' Rechnungsdatum range, for the confirm step (`YYYY-MM-DD`). */
export function selectedDateRange(
  preview: Pick<PeriodBackfillPreviewDto, 'candidates'>,
  selected: ReadonlySet<string>,
  timeZone: string,
): { readonly from: string; readonly to: string } | null {
  const days = preview.candidates
    .filter((candidate) => selected.has(candidate.orderId))
    .map((candidate) => dayInZone(new Date(candidate.processedAt), timeZone))
    .sort();
  if (days.length === 0) return null;
  return { from: days[0], to: days[days.length - 1] };
}

/** Counts per outcome, for the result banner. */
export function summarizeResults(
  results: readonly PeriodBackfillOrderResultDto[],
): Record<PeriodBackfillOrderOutcome, number> {
  const counts: Record<PeriodBackfillOrderOutcome, number> = {
    [PeriodBackfillOrderOutcome.Minted]: 0,
    [PeriodBackfillOrderOutcome.Refused]: 0,
    [PeriodBackfillOrderOutcome.Failed]: 0,
  };
  for (const result of results) counts[result.outcome] += 1;
  return counts;
}
